import React, { useState, useRef, useEffect } from 'react';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Input,
    Table,
    FormGroup,
    Label,
    Spinner,
    Alert,
    Card,
    CardBody,
    Row,
    Col,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    InputGroup,
    InputGroupText
} from 'reactstrap';
import axios from 'axios';

interface ConsigneLine {
    line_id: string;
    description: string;
    qty: number;
    max_qty: number;
    total_ttc: number;
    total_ht: number;
    fk_product: string;
    product_label: string;
    product_ref: string;
}

interface InvoiceData {
    facture: {
        date: string;
        date_lim_reglement: string;
        id: string;
        ref: string;
        total_ht: string;
        total_ttc: string;
    };
    filtre_type: number;
    lines: ConsigneLine[];
    message: string;
    success: boolean;
}

interface InvoiceListItem {
    id: number;
    ref: string;
    type: string;
    label: string;
}

interface SearchResponse {
    success: boolean;
    message: string;
    data: InvoiceListItem[];
    term: string;
    entity: number;
    entityname: string;
}

interface RetourConsigneModalProps {
    isOpen: boolean;
    toggle: () => void;
    user: any;
}

const RetourConsigneModal: React.FC<RetourConsigneModalProps> = ({ isOpen, toggle, user }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<InvoiceListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [validating, setValidating] = useState(false);
    const [invoiceData, setInvoiceData] = useState<InvoiceData | null>(null);
    const [selectedInvoice, setSelectedInvoice] = useState<InvoiceListItem | null>(null);
    const [quantites, setQuantites] = useState<{ [key: string]: number }>({});
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pdfUrl, setPdfUrl] = useState<string>('');
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const searchRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchList = async (search: string) => {
        if (search.length < 3) {
            setSearchResults([]);
            setDropdownOpen(false);
            return;
        }

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Set new timeout for debouncing
        searchTimeoutRef.current = setTimeout(async () => {
            setSearchLoading(true);
            setDropdownOpen(true);

            const data = {
                action: 'searchref',
                entity: user.entity,
                type: "facture",
                filters :{ "type": 0 } ,
                term: search.trim()
            };

            try {
                const response : any = await axios.post<SearchResponse>(
                    'https://api.apapiecesauto.fr/apiV2/',
                    data,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${user.token}`,
                        }
                    }
                );

                if (response.success) {
                    setSearchResults(response.data);
                    setError('');
                } else {
                    setSearchResults([]);
                    setError(response.message || 'Aucune facture trouvée');
                }
            } catch (error) {
                console.error('Error searching invoices:', error);
                setSearchResults([]);
                setError('Erreur lors de la recherche des factures');
            } finally {
                setSearchLoading(false);
            }
        }, 300); // 300ms debounce delay
    };

    const handleInvoiceSelect = async (invoice: InvoiceListItem) => {
        setSelectedInvoice(invoice);
        setSearchTerm(invoice.ref);
        setSearchResults([]);
        setDropdownOpen(false);
        setError('');
        await handleSearch(invoice.id);
    };

    const handleSearch = async (invoiceId: number) => {
        
        if (!invoiceId) {
            setError('Veuillez sélectionner une facture');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setInvoiceData(null);
        setQuantites({});
        setPdfUrl('');
        setShowPdfModal(false);
        
        const data = {
            action: 'invoicedetails',
            entity: user.entity,
            filtre_type: 1,
            id: invoiceId
        };

        try {
            const response :any = await axios.post<InvoiceData>(
                'https://api.apapiecesauto.fr/apiV2/',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );

            if (response.success) {
                setInvoiceData(response);

                // Initialize quantities with 0
                const initialQuantites: { [key: string]: number } = {};
                response.lines.forEach((line: ConsigneLine) => {
                    initialQuantites[line.line_id] = 0;
                });
                setQuantites(initialQuantites);

            } else {
                setError('Aucune donnée de facture trouvée');
            }
        } catch (error) {
            console.error('Error fetching invoice details:', error);
            setError('Erreur lors de la récupération des détails de la facture');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadPdf = () => {
        if (pdfUrl) {
            window.open(pdfUrl, '_blank');
        }
    };

    const handleInputFocus = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (quantites[lineId] === 0) {
            e.target.value = '';
        }
    };

    const handleInputBlur = (lineId: string, e: React.FocusEvent<HTMLInputElement>) => {
        if (e.target.value === '') {
            setQuantites(prev => ({
                ...prev,
                [lineId]: 0
            }));
        }
    };

    const handleQuantiteChange = (lineId: string, value: string) => {
        if (value === '') {
            setQuantites(prev => ({
                ...prev,
                [lineId]: 0
            }));
            return;
        }

        const numValue = parseInt(value);
        if (isNaN(numValue)) return;

        const line = invoiceData?.lines.find(l => l.line_id === lineId);

        if (line) {
            const maxQty = line.max_qty;
            if (numValue >= 0 && numValue <= maxQty) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: numValue
                }));
            } else if (numValue > maxQty) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: maxQty
                }));
            } else if (numValue < 0) {
                setQuantites(prev => ({
                    ...prev,
                    [lineId]: 0
                }));
            }
        }
    };

    const handleValidate = async () => {
        if (!invoiceData) {
            setError('Aucune facture sélectionnée');
            return;
        }

        const hasQuantities = invoiceData.lines.some(line => {
            const qty = quantites[line.line_id] || 0;
            return qty > 0 && line.max_qty > 0;
        });

        if (!hasQuantities) {
            setError('Veuillez spécifier au moins une quantité à retourner');
            return;
        }

        setValidating(true);
        setError('');
        setSuccess('');

        try {
            const itemsToReturn = invoiceData.lines
                .filter(line => line.max_qty > 0 && (quantites[line.line_id] || 0) > 0)
                .map(line => ({
                    line_id: line.line_id,
                    qt: quantites[line.line_id],
                }));

            if (itemsToReturn.length === 0) {
                setError('Veuillez spécifier au moins une quantité à retourner');
                setValidating(false);
                return;
            }

            const data = {
                action: 'retourConsigne',
                entity: user.entity,
                lines: itemsToReturn,
            };

            const response :any = await axios.post<{ success: boolean; message: string; resultPDF?: string }>(
                'https://api.apapiecesauto.fr/apiV2/',
                data,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user.token}`,
                    }
                }
            );
            console.log('Retour Consigne Response:', response);

            if (response.success) {
                const totalAmount = calculateTotal();
                setSuccess(`Retour de consigne validé avec succès! Montant remboursé: ${totalAmount.toFixed(2)} €`);

                if (response.resultPDF) {
                    setPdfUrl(response.resultPDF);
                }

            } else {
                setError(response.message || 'Erreur lors de la validation du retour');
            }
            
        } catch (error) {
            console.error('Error validating retour consigne:', error);
            setError('Erreur lors de la validation du retour');
        } finally {
            setValidating(false);
        }
    };

    const calculateTotal = () => {
        if (!invoiceData) return 0;

        return invoiceData.lines.reduce((total, line) => {
            return total + (quantites[line.line_id] * line.total_ttc);
        }, 0);
    };

    const resetModal = () => {
        setSearchTerm('');
        setSearchResults([]);
        setSelectedInvoice(null);
        setInvoiceData(null);
        setQuantites({});
        setError('');
        setSuccess('');
        setPdfUrl('');
        setShowPdfModal(false);
        setLoading(false);
        setSearchLoading(false);
        setValidating(false);
        setDropdownOpen(false);
        toggle();
    };

    const openPdfModal = () => {
        setShowPdfModal(true);
    };

    const closePdfModal = () => {
        setShowPdfModal(false);
    };

    return (
        <Modal isOpen={isOpen} toggle={resetModal} size="lg" centered>
            <ModalHeader toggle={resetModal}>
                Retour de Consigne
            </ModalHeader>

            <ModalBody>
                {/* Search Section with Dropdown */}
                <FormGroup>
                    <Label for="searchConsigne" className="fw-bold mb-2">
                        Rechercher une Facture
                    </Label>
                    <div className="position-relative" ref={searchRef}>
                        <InputGroup>
                            <InputGroupText className="bg-light">
                                <i className="ri-search-line"></i>
                            </InputGroupText>
                            <Input
                                type="text"
                                id="searchConsigne"
                                placeholder="Entrez au moins 3 caractères..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    handleSearchList(e.target.value);
                                }}
                                disabled={loading || validating}
                                className="border-start-0"
                            />
                            {searchLoading && (
                                <InputGroupText className="bg-white border-start-0">
                                    <Spinner size="sm" />
                                </InputGroupText>
                            )}
                        </InputGroup>

                        {/* Search Results Dropdown */}
                        {dropdownOpen && searchResults.length > 0 && (
                            <div
                                className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                                style={{
                                    zIndex: 1000,
                                    maxHeight: '250px',
                                    overflowY: 'auto'
                                }}
                            >
                                <div className="p-1">
                                    {searchResults.map((invoice) => (
                                        <div
                                            key={invoice.id}
                                            className="p-2 border-bottom hover-pointer"
                                            onClick={() => handleInvoiceSelect(invoice)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <strong className="text-primary">{invoice.ref}</strong>
                                                    <div className="small text-muted">{invoice.label}</div>
                                                </div>
                                                <i className="ri-arrow-right-s-line text-muted"></i>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {dropdownOpen && searchTerm.length >= 3 && !searchLoading && searchResults.length === 0 && (
                            <div
                                className="position-absolute w-100 bg-white border rounded shadow-sm mt-1 p-3 text-center"
                                style={{ zIndex: 1000 }}
                            >
                                <i className="ri-search-line fs-4 text-muted mb-2 d-block"></i>
                                <div className="text-muted small">Aucune facture trouvée</div>
                            </div>
                        )}
                    </div>
                    {selectedInvoice && (
                        <div className="mt-2">
                            <Alert color="info" className="py-2 mb-0">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Facture sélectionnée :</strong> {selectedInvoice.ref}
                                    </div>
                                    <Button
                                        color="link"
                                        size="sm"
                                        onClick={() => {
                                            setSelectedInvoice(null);
                                            setSearchTerm('');
                                            setInvoiceData(null);
                                        }}
                                        className="p-0"
                                    >
                                        <i className="ri-close-line"></i>
                                    </Button>
                                </div>
                            </Alert>
                        </div>
                    )}
                </FormGroup>

                {/* Alerts */}
                {error && <Alert color="danger" className="py-2">{error}</Alert>}
                {success && <Alert color="success" className="py-2">{success}</Alert>}

                {/* Loading Spinner for Invoice Details */}
                {loading && (
                    <div className="text-center my-4">
                        <Spinner color="primary" />
                        <div className="mt-2 text-muted">Chargement des détails de la facture...</div>
                    </div>
                )}

                {/* Invoice Information */}
                {invoiceData && !loading && (
                    <Card className="mt-3 border-primary">
                        <CardBody>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <strong>Facture:</strong> {invoiceData.facture?.ref || 'N/A'}
                                </Col>
                                <Col md={6}>
                                    <strong>Date:</strong> {invoiceData.facture?.date || 'N/A'}
                                </Col>
                            </Row>

                            {/* PDF Download inside card */}
                            {pdfUrl && !showPdfModal && (
                                <div className="mb-3">
                                    <Button
                                        color="danger"
                                        onClick={() => setShowPdfModal(true)}
                                    >
                                        <i className="ri-file-pdf-line me-1"></i>
                                        Afficher la Facture PDF
                                    </Button>
                                </div>
                            )}

                            <div className="table-responsive">
                                <Table bordered size="sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Description</th>
                                            <th className="text-center">Prix Consigne</th>
                                            <th className="text-center">Qte Facturée</th>
                                            <th className="text-center">Qte à Retourner</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            if (!invoiceData.lines || !Array.isArray(invoiceData.lines)) {
                                                return (
                                                    <tr>
                                                        <td colSpan={4} className="text-center py-4">
                                                            <div className="text-muted">
                                                                <i className="ri-alert-line fs-3 d-block mb-2"></i>
                                                                Aucune donnée de ligne disponible
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            const filteredLines = invoiceData.lines.filter(line => line.max_qty > 0);

                                            if (filteredLines.length === 0) {
                                                return (
                                                    <tr>
                                                        <td colSpan={4} className="text-center py-4">
                                                            <div className="text-muted">
                                                                <i className="ri-inbox-line fs-3 d-block mb-2"></i>
                                                                Aucun article disponible pour retour
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return filteredLines.map((line) => (
                                                <tr key={line.line_id}>
                                                    <td style={{ maxWidth: '200px' }}>
                                                        <small>{line.description}</small>
                                                    </td>
                                                    <td className="text-success fw-bold text-center">
                                                        {line.total_ttc?.toFixed(2) || '0.00'} €
                                                    </td>
                                                    <td className="text-center">
                                                        {line.qty || 0}
                                                    </td>
                                                    <td style={{ width: '120px' }}>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            max={line.max_qty || 0}
                                                            value={quantites[line.line_id] === 0 ? 0 : quantites[line.line_id]}
                                                            onChange={(e) => handleQuantiteChange(line.line_id, e.target.value)}
                                                            onFocus={(e) => handleInputFocus(line.line_id, e)}
                                                            onBlur={(e) => handleInputBlur(line.line_id, e)}
                                                            disabled={validating || !!pdfUrl}
                                                            className="text-center"
                                                            style={{
                                                                textAlign: 'center',
                                                                paddingLeft: '0',
                                                                paddingRight: '0'
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                </Table>

                                {/* PDF Modal */}
                                <Modal isOpen={showPdfModal} toggle={closePdfModal} size="xl" centered>
                                    <ModalHeader toggle={closePdfModal}>
                                        <i className="ri-file-pdf-line me-2 text-danger"></i>
                                        Facture PDF
                                    </ModalHeader>
                                    <ModalBody className="p-0" style={{ height: '70vh' }}>
                                        {pdfUrl && (
                                            <iframe
                                                src={pdfUrl}
                                                title="Facture PDF"
                                                width="100%"
                                                height="100%"
                                                style={{ border: 'none' }}
                                            />
                                        )}
                                    </ModalBody>
                                    <ModalFooter>
                                        <Button
                                            color="danger"
                                            onClick={() => {
                                                if (pdfUrl) {
                                                    window.open(pdfUrl, '_blank');
                                                }
                                            }}
                                        >
                                            <i className="ri-download-line me-1"></i>
                                            Télécharger
                                        </Button>
                                    </ModalFooter>
                                </Modal>
                            </div>

                            {/* Total Calculation */}
                            {(() => {
                                const hasLinesWithMaxQty = invoiceData.lines?.some(line => line.max_qty > 0);

                                if (!hasLinesWithMaxQty) {
                                    return null;
                                }

                                return (
                                    <div className="mt-3 p-3 bg-light rounded">
                                        <Row className="text-center">
                                            <Col md={6}>
                                                <div className="text-muted small">Montant total à rembourser</div>
                                                <div className="h3 text-success fw-bold mb-0">
                                                    {calculateTotal().toFixed(2)} €
                                                </div>
                                            </Col>

                                            <Col md={6}>
                                                {invoiceData && !pdfUrl && (
                                                    <Button
                                                        color="success"
                                                        onClick={handleValidate}
                                                        disabled={validating}
                                                        className="w-100"
                                                    >
                                                        {validating ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Validation...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="ri-check-line me-2"></i>
                                                                Valider le Retour
                                                            </>
                                                        )}
                                                    </Button>
                                                )}
                                                {pdfUrl && (
                                                    <div className="text-success">
                                                        <i className="ri-checkbox-circle-fill me-2"></i>
                                                        Retour validé
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                );
                            })()}
                        </CardBody>
                    </Card>
                )}
            </ModalBody>
        </Modal>
    );
};

export default RetourConsigneModal;